import { useEffect, useMemo, useState, useRef } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box, Backdrop, CircularProgress, Checkbox, FormControlLabel, IconButton, Tooltip } from "@mui/material";
import { FilterAlt } from "@mui/icons-material";
import { blue, lightBlue, red} from '@mui/material/colors';

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {cloneDeep, findIndex, isEmpty, isEqual, isNull, isUndefined, size} from "lodash";

import SplitterContainer from "@plannel/components/SplitterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import { RPFilterModal } from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/FilterModal";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Dialog from "@plannel/components/Dialog"
import Snackbar from "@plannel/components/Snackbar";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import {SaveButton, FilterButton, RunButton} from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";

import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import "@plannel/scss/components/CustomGrid.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import p13nService from "@plannel/services/system/p13n-service";
import DimensAttrModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/DimensAttrModal";
import FilterStatusPanel, {extractFilterInfo, getFilterStore} from "@plannel/components/aggrid/FilterStatusPanel";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";
import rpChannelPlanningReviewService from "@plannel/services/rp/rp-channel-planning-review-service";
import moment from "moment/moment";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import ZeroExclusionFilter from "@plannel/components/filter/ZeroExclusionFilter";
import companyService from "@plannel/services/data/company-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import measureService from "@plannel/services/data/measure-service";
import demandValService from "@plannel/services/data/demand-val-service";
import runRpService from "@plannel/services/rp/run-rp-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import RunModal from "@plannel/components/modal/RunModal";
import { reloadRpVersionFilter } from "@plannel/components/filter/RpVersionFilter";

const { snackBarMessageColor } = ColorUtil;
const { CHECKBOX_SELECTED } = GridUtils.rowSelectedSource;

// const FINITE = "finite";
const { dpABCConfig, dpMeasure, rpMeasure, versionStatusCode, muicolor, rpPolicyCd, item, customer, filterType, buckets: { DAY } } = CodeUtil;
const { SA_QTY, ASA_QTY, MF_QTY, BF_QTY } = dpMeasure
const { SIMULATION, PLANNED, RELEASED, SCENARIO } = versionStatusCode;
const { DEMAND_QTY, KIT_FCST_DEMAND_QTY, FCST_DEMAND_QTY,INTERNAL_DEMAND_QTY, RP_ORDER, ARRIVAL_QTY, ARRIVAL_QTY_PLAN, ARRIVAL_QTY_CFM, SHIPMENT_QTY, SHIPMENT_QTY_PLAN, SHIPMENT_QTY_CFM, SHIPMENT_QTY_FCST, SHIPMENT_QTY_REPL, BOH, EOH, TARGET_STOCK, ROP , BACKORDER_QTY , INV_COV_DAYS } = rpMeasure;
const { P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM } = rpPolicyCd;
const RP_POLICY_LIST = [P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM];

const MAIN_GRID = "mainGrid";
const SUB_GRID = "subGrid";
const bucketDaysObj = {
  day: 1,
  week: 7,
  month: 30,
};

const ChannelPlanningReview = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [20, 50, 100, 200, 500];
  const resultTotalCount = useRef(null);

  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [subCurrentPage, setSubCurrentPage] = useState(reduxViewState?.subCurrentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [subPageSize, setSubPageSize] = useState(reduxViewState?.subPageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);
  const [subGridPages, setSubGridPages] = useState(0);

  const [sortParams, setSortParams] = useState({});
  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ? reduxViewState.rpVersion : null);
  const [dpVersionStatusCd, setDpVersionStatusCd] = useState(null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const [appliedExceptions, setAppliedExceptions] = useState(reduxViewState?.appliedExceptions ?? []);
  const [onlyExceptionApplied, setOnlyExceptionApplied] = useState(reduxViewState?.onlyExceptionApplied ?? false);
  const selectedCellId = useRef([]);

  const [backDropOpen, setBackDropOpen] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [selectItemHrchy, setSelectItemHrchy] = useState([]);
  const [isRun, setIsRun] = useState(false);
  const [defaultValue, setDefaultValue] = useState([]);
  const [useShipmentTxn, setUseShipmentTxn] = useState(false);
  const [useMpProductionPlan, setUseMpProductionPlan] = useState(false);

  const [simulationData, setSimulationData] = useState([]);
  const cubeHeaders = useRef([]);
  const gridHeaderMap = useRef({});
  const subGridHeaderMap = useRef({});
  const simulationUpdateDatasRef = useRef([]);
  const dpVersionRef = useRef(null);

  const mainGridRef = useRef();
  const subGridRef = useRef();
  const mainBucketColumnDefs = useRef([]);
  const subBucketColumnDefs = useRef([]);
  const [gridLoading, setGridLoading] = useState(false);
  const itemColsHide = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "itemColsHide") ? reduxViewState.itemColsHide : false;

  // item / customer attribute
  const [dimenModalOpen, setDimenModalOpen] = useState(false);
  const [dimenAttrTarget, setDimenAttrTarget] = useState("item");
  const [dimensionAttrData, setDimensionAttrData] = useState(reduxViewState?.dimensionAttrData ?? null);
  const [attrConfig, setAttrConfig] = useState(null);

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);
  const [p13Measures, setP13Measures] = useState([]);
  const [subAllMeasure, setSubAllMeasure] = useState([]);
  const [subAllMeasureObj, setSubAllMeasureObj] = useState([]);
  const [subMeasureModalOpen, setSubMeasureModalOpen] = useState(false);
  const [subMeasures, setSubMeasures] = useState([SA_QTY.cd, ASA_QTY.cd, BF_QTY.cd, MF_QTY.cd]);
  const allMeasure = CodeUtil.rpChannelPlanningReviewMeasures;
  const defaultMeasure = CodeUtil.rpChannelPlanningReviewDefaultMeasures;

  const [displayName, setDisplayName] = useState(null);
  const [subGridData, setSubGridData] = useState([]);
  const [mfMeasure, setMfMeasure] = useState(null);

  // advanced filter
  const [advancedFilters, setAdvancedFilters] = useState(
    reduxViewState?.advancedFilters?.children?.length > 0 ? reduxViewState.advancedFilters : null
  );
  const [pendingAdvancedFilters, setPendingAdvancedFilters] = useState(null);
  const [isAFActive, setIsAFActive] = useState(!!(reduxViewState?.advancedFilters?.children?.length > 0));
  const [openFilter, setOpenFilter] = useState(false);
  const [initLoad, setInitLoad] = useState(false);

  const [itemFilterColor, setItemFilterColor] = useState(size(reduxViewState?.itemFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
  const [itemFilterList, setItemFilterList] = useState(reduxViewState?.itemFilterList ?? {});
  const [itemFilterType, setItemFilterType] = useState(reduxViewState?.itemFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [itemCustomValue, setItemCustomValue] = useState(
    Array.isArray(reduxViewState?.filter?.category) && reduxViewState.filter.category.length > 0
      ? reduxViewState.filter.category
      : null
  );
  const [treeLoad, setTreeLoad] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [customerFilterColor, setCustomerFilterColor] = useState(size(reduxViewState?.customerFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
  const [customerFilterList, setCustomerFilterList] = useState(reduxViewState?.customerFilterList ?? {});
  const [custFilterType, setCustFilterType] = useState(reduxViewState?.custFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [customerCustomValue, setCustomerCustomValue] = useState(
    Array.isArray(reduxViewState?.filter?.customerHrchys) && reduxViewState.filter.customerHrchys.length > 0
      ? reduxViewState.filter.customerHrchys
      : null
  );
  const [custTreeLoad, setCustTreeLoad] = useState(false);
  const [customerFilterData, setCustomerFilterData] = useState({});
  const [customerFilterModalOpen, setCustomerFilterModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(reduxViewState?.itemCd);
  const [selectedLoc, setSelectedLoc] = useState(reduxViewState?.locCd);
  const [selectedCust, setSelectedCust] = useState(reduxViewState?.customerCd);

  const advancedFilterColumnDefs = useMemo(() => {
    const createAttributeColumns = (selectedAttrs, scope, groupLabel, configMap) => Array.from({ length: 10 }, (_, idx) => {
      const attrCd = selectedAttrs[idx];
      const attrOrder = idx + 1;
      const scopedAttrName = `${scope}Attr${attrOrder}`;

      return {
        headerName: attrCd ? (configMap?.[attrCd] ?? attrCd) : `attr${attrOrder}`,
        groupLabel,
        filterName: scopedAttrName,
        filterScope: scope,
        filterType: "string"
      };
    });

    const attributeNames = ["attr01", "attr02", "attr03", "attr04", "attr05", "attr06", "attr07", "attr08", "attr09", "attr10"];
    const customerAttributeColumns = createAttributeColumns(attributeNames, "cust", `${t("customer")} ${t("attr")}`, attrConfig?.customer);
    const itemAttributeColumns = createAttributeColumns(attributeNames, "item", `${t("item")} ${t("attr")}`, attrConfig?.item);

    return [
      { headerName: "originLocation", filterType: "string" },
      { headerName: "destLocation", filterType: "string" },
      { headerName: "itemCd", filterType: "string" },
      { headerName: "itemName", filterType: "string" },
      { headerName: "invClassCd", filterType: "string", filterOption: dpABCConfig },
      { headerName: "category", filterType: "string" },
      { headerName: "customerCd", filterType: "string" },
      { headerName: "customerName", filterType: "string" },
      { headerName: "customerHrchy", filterType: "string" },
      { headerName: "orderMethod", filterType: "string", filterOption: RP_POLICY_LIST },
      { headerName: "targetStock", filterType: "number" },
      { headerName: "rop", filterType: "number" },
      { headerName: "lastMInvDays", filterType: "number" },
      { headerName: "orderCalendar", filterType: "string" },
      { headerName: "moq", filterType: "number" },
      { headerName: "ioq", filterType: "number" },
      { headerName: "otdLtD", filterType: "number" },
      { headerName: "excludeCondition", filterType: "select", filterOption: ["zeroReplenishment", "zeroPurchase", "zeroDemand", "zeroEoh", "exceptionsOnly"] },
      ...customerAttributeColumns,
      ...itemAttributeColumns,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dpABCConfig, attrConfig]);

  // 이미 advanced filter가 적용된 상태가 아니면, 상단 필터들을 advanced filter 조건으로 변환해 초기값으로 채운다
  const scopedAttrMap = scope => Object.fromEntries(Array.from({ length: 10 }, (_, idx) => (
    [`attr${String(idx + 1).padStart(2, "0")}`, { headerName: `${scope}Attr${idx + 1}`, filterScope: scope }]
  )));
  const ITEM_SEARCH_TO_AF = {
    itemCd: { headerName: "itemCd" },
    name: { headerName: "itemName" },
    itemHrchy: { headerName: "category" },
    invClassCd: { headerName: "invClassCd" },
    ...scopedAttrMap("item"),
  };
  const CUSTOMER_SEARCH_TO_AF = {
    customerCd: { headerName: "customerCd" },
    name: { headerName: "customerName" },
    customerHrchy: { headerName: "customerHrchy" },
    ...scopedAttrMap("cust"),
  };

  const unsupportedKeys = (list, map) => Object.keys(list ?? {}).filter(key => !isEmpty(list[key]) && !map[key]);

  const getUnsupportedSearchKeys = () => [
    ...(isEmpty(filterValue?.category) ? unsupportedKeys(itemFilterList, ITEM_SEARCH_TO_AF) : []),
    ...(isEmpty(filterValue?.customerHrchys) ? unsupportedKeys(customerFilterList, CUSTOMER_SEARCH_TO_AF) : []),
  ];

  const pushSearchNodes = (nodes, list, map, filterTypes) => {
    const [logic, compare] = filterTypes ?? [filterType.or.cd, filterType.equal.cd];
    const operator = compare === filterType.notEqual.cd ? "notEqual" : "equals";
    const logicOperator = logic === filterType.and.cd ? "AND" : "OR";
    const searchNodes = [];

    for (const [key, values] of Object.entries(list ?? {})) {
      const col = map[key];
      const targets = (values ?? []).filter(Boolean);
      if (!col || targets.length === 0) {
        continue;
      }
      const leaf = value => ({ ...col, filterType: "string", operator, values: [value] });
      if (targets.length > 1) {
        searchNodes.push({ logicOperator, children: targets.map(leaf) });
      } else {
        searchNodes.push(leaf(targets[0]));
      }
    }

    if (searchNodes.length > 1) {
      nodes.push({ logicOperator, children: searchNodes });
    } else if (searchNodes.length === 1) {
      nodes.push(searchNodes[0]);
    }
  };

  const buildInitialFromCurrentFilters = () => {
    const nodes = [];
    const destLocs = filterValue?.locations?.map(l => l.locCd).filter(Boolean) ?? [];
    const categories = filterValue?.category?.filter(c => c.leafFlg).map(c => c.hrchyCd) ?? [];
    const custHrchys = filterValue?.customerHrchys?.filter(c => c.leafFlg).map(c => c.hrchyCd) ?? [];
    const exceptions = [...(appliedExceptions ?? [])];

    if (onlyExceptionApplied && !exceptions.includes("exceptionsOnly")) {
      exceptions.push("exceptionsOnly");
    }

    if (destLocs.length > 1) {
      nodes.push({ logicOperator: "OR", children: destLocs.map(cd => ({ filterType: "string", headerName: "destLocation", operator: "equals", values: [cd] })) });
    } else if (destLocs.length === 1) {
      nodes.push({ filterType: "string", headerName: "destLocation", operator: "equals", values: [destLocs[0]] });
    }

    if (categories.length > 1) {
      nodes.push({ logicOperator: "OR", children: categories.map(cd => ({ filterType: "string", headerName: "category", operator: "equals", values: [cd] })) });
    } else if (categories.length === 1) {
      nodes.push({ filterType: "string", headerName: "category", operator: "equals", values: [categories[0]] });
    } else {
      pushSearchNodes(nodes, itemFilterList, ITEM_SEARCH_TO_AF, itemFilterType);
    }

    if (custHrchys.length > 1) {
      nodes.push({ logicOperator: "OR", children: custHrchys.map(cd => ({ filterType: "string", headerName: "customerHrchy", operator: "equals", values: [cd] })) });
    } else if (custHrchys.length === 1) {
      nodes.push({ filterType: "string", headerName: "customerHrchy", operator: "equals", values: [custHrchys[0]] });
    } else {
      pushSearchNodes(nodes, customerFilterList, CUSTOMER_SEARCH_TO_AF, custFilterType);
    }

    for (const ex of exceptions) {
      nodes.push({ filterType: "select", headerName: "excludeCondition", filterOption: ["zeroReplenishment", "zeroPurchase", "zeroDemand", "zeroEoh", "exceptionsOnly"], operator: "equals", values: [ex] });
    }

    return nodes.length > 0 ? { logicOperator: "AND", children: [{ logicOperator: "AND", children: nodes }] } : { logicOperator: "AND", children: [] };
  };

  const handleAdvancedFilterApply = (filters) => {
    const normalized = filters?.children?.length > 0 ? filters : null;
    setFilterValue({ originLocation: null, destLocation: null, category: null, customerHrchys: null});
    setItemFilterList({});
    setCustomerFilterList({});
    setAppliedExceptions([]);
    setOnlyExceptionApplied(false);
    setAdvancedFilters(normalized);
    setPendingAdvancedFilters(normalized);
    setIsAFActive(!!normalized);
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createFilterViewState(viewName, null)));
  };

  const handleFilterOpen = () => {
    if (!openFilter) {
      setPendingAdvancedFilters(isAFActive ? null : buildInitialFromCurrentFilters());

      const unsupported = isAFActive ? [] : getUnsupportedSearchKeys();
      if (unsupported.length > 0) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_UnsupportedItemFilterInAdvancedFilter", { fields: unsupported.map(key => t(key)).join(", ") }) });
      }
    }
    setOpenFilter(!openFilter);
  };

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };

  const filterActionItem = (params) => {
    if (params.type === "click") {
      setFilterModalOpen(true);
    }
  };

  const handelCustomerFilterClose = () => {
    setCustomerFilterModalOpen(false);
  };

  const filterActionCustomer = (params) => {
    if (params.type === "click") {
      setCustomerFilterModalOpen(true);
    }
  };

  const handleAppliedExceptionsChange = (newList) => {
    setAppliedExceptions(newList);
  };

  const handleExceptionsOnlyChange = (e) => {
    setOnlyExceptionApplied(e.target.checked);
  };

  const getInvCovDays = (startIdx, headers, resultsData, isSimulation) => {
    const bucketType = rpVersionFilter?.planConfigObj?.BUCKET_UNIT;
    const bucketDays = bucketDaysObj[bucketType] || 1;

    const eohQtyPivotData = isSimulation ? "eohQtyAdjPivotData" : "eohQtyPivotData";
    const demandQtyPivotData = isSimulation ? "demandQtyAdjPivotData" : "demandQtyPivotData";

    let invCovDays = 0;
    let eoh = resultsData[eohQtyPivotData][startIdx];
    const endIdx = headers.length;

    for (let i = startIdx + 1; i < endIdx; i++) {
      const demandQty = resultsData[demandQtyPivotData][i];
      const leftStock = eoh - demandQty;

      if (leftStock >= 0) {
        eoh = leftStock;
        invCovDays += bucketDays;
      } else {
        const demandPerDay = Math.max(demandQty / bucketDays, 1);
        invCovDays += Math.floor(eoh / demandPerDay);
        break;
      }
    }

    return invCovDays;
  }

  const cellStyleByMeasure = params => {
    if (params?.data?.measure) {
      const { measure } = params.data;
      if (isEqual(measure, mfMeasure?.name) && isEqual(dpVersionStatusCd, SCENARIO)) {
        return { background: lightBlue[50] };
      }
    }

    return {};
  }

  const findMeasureData = (nodes, measure) => {
    for (const node of nodes) {
      if (node && isEqual(node.data.measure, measure)) {
        return node.data;
      }
    }
    return {};
  }

  const dataCellStyle = (params, headers) => {
    const cellStyle = cellStyleByMeasure(params);
    const { column, data, node, value } = params;
    if (data && column) {
      const { measure } = data;
      if (measure && column?.colId) {
        const replacePostFix = rpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + rpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
        const replaceColId = column?.colId.replace(replacePostFix, "");
        const headerIndex = headers.indexOf(replaceColId);
        if (headerIndex !== -1 && value !== null) {
          const locationItemNodes = node.parent.allLeafChildren;
          const numValue = Number(value);

          const demnadData = findMeasureData(locationItemNodes, DEMAND_QTY);
          const demandQty = demnadData[headers[headerIndex]];

          const isException = isEqual(measure, SHIPMENT_QTY) && demandQty > numValue;

          if (isException) {
            cellStyle.background = red[100];
            cellStyle.fontStyle = 'italic';
          }

          const cloneData = cloneDeep(data);
          cloneData.baseDate = replaceColId;

          const useShipmentTxn = rpVersionFilter?.planConfigObj?.USE_SHIPMENT_TXN;

          if (useShipmentTxn) {
            const planExecDate = rpVersionFilter?.planConfigObj?.PLAN_EXEC_DATE ?? moment().format('YYYY-MM-DD');
            const baseDate = replaceColId;

            if (planExecDate && baseDate) {
              const bucketUnit = rpVersionFilter?.planConfigObj?.BUCKET_UNIT || DAY;
              const bucket = moment(baseDate);
              const execDate = moment(planExecDate);
              if (bucket.isSameOrBefore(execDate, bucketUnit)) {
                cellStyle.background = null;
              }
            }
          }
        }
      }
    }
    return cellStyle;
  }

  const subGridCellStyle = (params) => {
    const cellStyle = cellStyleByMeasure(params);
    const { data, column } = params;

    if (data?.edited && column?.colDef?.field) {
      const headerIndex = findIndex(cubeHeaders.current, header => isEqual(header, column.colDef.field));
      if (headerIndex > -1 && data.edited[headerIndex] !== undefined) {
        cellStyle.color = blue.A700;
        cellStyle.fontWeight = 'bold';
      }
    }

    return cellStyle;
  }

  const emptyValueFormatter = params => {
    if (isNull(params.value) || isUndefined(params.value)) {
      return "";
    }

    return NumberUtil.formatNumberData(params, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }

  const getAttributeColDefs = (isExcel = false) => {
    const itemColDefs = [];
    const attrDatas = dimensionAttrData?.attr;

    if (!attrDatas) {
      return itemColDefs;
    }

    for (let i = 0; i < attrDatas?.length; i ++) {
      const attrData = attrDatas[i];
      const attrId = `prod_${attrData}`;
      const sort = isEqual(sortParams?.orderByColumn, attrId) ? sortParams?.sortType : null;
      const attrNm = attrConfig?.item ? attrConfig.item[attrData] ?? attrData : attrData;
      const itemAttr = "itemAttr";
      const fieldNm = itemAttr + i;

      const itemAttrArry = isExcel ? [
        {
          headerName: attrNm,
          colId: attrId,
          field: fieldNm,
          cellClass: "stringType",
          width: 120,
        },
        {
          field: fieldNm,
          editable: false,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
        }] : [{
        headerName: attrNm,
        colId: attrId,
        showRowGroup: fieldNm,
        chartDataType: "category",
        pinned: "left", lockPinned: true,
        cellRenderer: "agGroupCellRenderer",
        cellClass: "stringType",
        cellRendererParams: {
          suppressCount: true,
        },
        width: 120,
        sortable: true,
        sort
      },
        {
          field: fieldNm,
          editable: false,
          pinned: "left", lockPinned: true,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
          sort
        }
      ];
      itemColDefs.push(...itemAttrArry);
    }
    return itemColDefs;
  }

  const getCustomerAttributeColDefs = (isExcel = false) => {
    const custColDefs = [];
    const attrDatas = dimensionAttrData?.custAttr;

    if (!attrDatas) {
      return custColDefs;
    }

    for (let i = 0; i < attrDatas?.length; i ++) {
      const attrData = attrDatas[i];
      const attrId = `cust_${attrData}`;
      const sort = isEqual(sortParams?.orderByColumn, attrId) ? sortParams?.sortType : null;
      const attrNm = attrConfig?.customer ? attrConfig.customer[attrData] ?? attrData : attrData;
      const custAttr = "custAttr";
      const fieldNm = custAttr + i;

      const custAttrArry = isExcel ? [
        {
          headerName: attrNm,
          colId: attrId,
          field: fieldNm,
          cellClass: "stringType",
          width: 120,
        },
        {
          field: fieldNm,
          editable: false,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
        }] : [{
        headerName: attrNm,
        colId: attrId,
        showRowGroup: fieldNm,
        chartDataType: "category",
        pinned: "left", lockPinned: true,
        cellRenderer: "agGroupCellRenderer",
        cellClass: "stringType",
        cellRendererParams: {
          suppressCount: true,
        },
        width: 120,
        sortable: true,
        sort
      },
        {
          field: fieldNm,
          editable: false,
          pinned: "left", lockPinned: true,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
          sort
        }
      ];
      custColDefs.push(...custAttrArry);
    }
    return custColDefs;
  }

  const defaultColumnDefs = useMemo(() => {

    const attributeColDefs = {
      // column header dimension modal
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isSorting: true,
        isClicked: () => {
          setDimenAttrTarget("item");
          setDimenModalOpen(true);
        }
      },
    }

    const customerAttributeColDefs = {
      // column header dimension modal
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isSorting: true,
        isClicked: () => {
          setDimenAttrTarget("customer");
          setDimenModalOpen(true);
        }
      },
    }

    const baseColumns = [
      {
        headerName: 'originLocation', colId: "originLocNmGrp", width: 140, showRowGroup: 'originLocNm', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
      },
      {
        headerName: 'destLocation', colId: "destLocNmGrp", width: 165, showRowGroup: 'destLocNm', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
      },
      {
        headerName: 'itemCd', colId: "itemCdGrp", cellClass: 'stringType', width: 160, showRowGroup: 'itemCd', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: { suppressCount: true },
        headerComponent: CustomDetailHeader,
        headerComponentParams: {
          viewName, reduxKey: 'itemColsHide', colsHide: itemColsHide,
          detailColumns: ["itemNmGrp"]
        },...attributeColDefs
      },
      {
        headerName: 'itemName', colId: "itemNmGrp", width: 220, showRowGroup: 'itemNm', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
      },
      {
        headerName: 'customer', colId: "customerNmGrp", width: 160, showRowGroup: 'customerNm', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
        ...customerAttributeColDefs
      },
      { headerName: "originLocation", field: "originLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "destLocation", field: "destLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "item", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "itemName", field: "itemNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "customer", field: "customerNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      {
        headerName: "measure", field: "measure", width: 170, type: ["nonEditableColumn"], pinned: "left", lockPinned: true,
        valueFormatter: params => t(params.value), cellStyle: cellStyleByMeasure, headerComponent: CustomHeaderMenuIcon,
        headerComponentParams: {
          enableMenu: false,
          isClicked: setMeasureModalOpen
        }
      }
    ];

    const itemColDefs = getAttributeColDefs();
    if (!isEmpty(itemColDefs)) {
      const itemIdx = baseColumns.findIndex(col => isEqual(col.colId, "itemCdGrp"));
      baseColumns.splice(itemIdx + 1, 0, ...itemColDefs);
    }

    const customerColDefs = getCustomerAttributeColDefs();
    if (!isEmpty(customerColDefs)) {
      const customerIdx = baseColumns.findIndex(col => isEqual(col.colId, "customerNmGrp"));
      baseColumns.splice(customerIdx + 1, 0, ...customerColDefs);
    }

    return GridUtils.getColumnDefs(baseColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionAttrData, filterValue, attrConfig, sortParams]);

  /*
   * 날짜(버킷) 컬럼은 조회 응답으로 만들어져 setGridHeaders 가 grid api 로 직접 밀어 넣는다.
   * 그래서 AgGridReact 의 columnDefs prop 이 바뀌면(advanced filter 적용 등으로 filterValue 가
   * 초기화되면 defaultColumnDefs 가 재계산된다) ag-grid 가 컬럼을 통째로 교체해 날짜 컬럼이 사라진다.
   * prop 재적용 때도 살아남도록 현재 버킷 컬럼을 항상 뒤에 붙여서 넘긴다.
   */
  const mainGridColumnDefs = useMemo(() => {
    return [...defaultColumnDefs, ...mainBucketColumnDefs.current];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultColumnDefs]);

  /* 하단 Sub Grid */
  const defaultSubGridColumnDefs = [
    {
      headerName: 'location', colId: "locNmGrp", width: 160, showRowGroup: 'locNm', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'itemCd', colId: "itemCdGrp", cellClass: 'stringType', width: 160, showRowGroup: 'itemCd', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, reduxKey: 'itemColsHide', colsHide: itemColsHide,
        detailColumns: ["itemNmGrp"]
      }
    },
    { headerName: "location", field: "locNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    {
      headerName: 'itemName', colId: "itemNmGrp", width: 220, showRowGroup: 'itemNm', pinned: "left", lockPinned: true, hide: itemColsHide,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'customer', colId: "customerNmGrp", width: 160, showRowGroup: 'customerNm', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    { headerName: "item", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "itemName", field: "itemNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "customer", field: "customerNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    {
      headerName: "measure", field: "measure", width: 170, type: ["nonEditableColumn"], pinned: "left", lockPinned: true,
      valueFormatter: params => t(params.value), cellStyle: cellStyleByMeasure,
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isClicked: setSubMeasureModalOpen
      }
    }
  ];

  const getRpRequestParams = (isPagination = true) => {
    const params = {};

    if (rpVersionFilter) {
      params.versionId = rpVersionFilter.id;
    }
    if (!isEmpty(advancedFilters)) {
      params.advancedFilters = advancedFilters;
    } else {
      if (filterValue?.locations) {
        params.destLocCd = filterValue?.locations?.map(location => location?.locCd);
      }
      if (!isEmpty(filterValue?.category)) {
        params.categoryCds = filterValue?.category?.filter((c) => c.leafFlg).map((c) => c.hrchyCd);
      } else if (itemFilterList && Object.keys(itemFilterList).length > 0) {
        params.itemFilterList = itemFilterList;
        params.itemFilterType = itemFilterType ?? [filterType.or.cd, filterType.equal.cd];
      }
      if (!isEmpty(filterValue?.customerHrchys)) {
        params.custHrchyCds = filterValue?.customerHrchys?.filter((c) => c.leafFlg).map((c) => c.hrchyCd);
      } else if (customerFilterList && Object.keys(customerFilterList).length > 0) {
        params.custFilterList = customerFilterList;
        params.custFilterType = custFilterType ?? [filterType.or.cd, filterType.equal.cd];
      }
      if (!isEmpty(appliedExceptions)) {
        params.appliedExceptions = appliedExceptions;
      }
    }

    if (isPagination) {
      params.page = currentPage ? currentPage - 1 : 0;
      params.pageSize = pageSize ?? 10;
    }

    if (!isEmpty(measures)) {
      params.measures = measures;
    } else if (!isEmpty(p13Measures)) {
      params.measures = p13Measures;
    } else {
      params.measures = allMeasure;
    }

    params.pagination = isPagination;
    params.totalCount = resultTotalCount.current;
    params.bucketType = rpVersionFilter?.planConfigObj?.BUCKET_UNIT;
    params.orderByItem = false;
    //sort
    const sort = sortParams?.orderByColumn;
    const colIds = defaultColumnDefs?.map(colDef => colDef.colId);
    if (sort && !colIds.includes(sort)) {
      return { ...params, ...(isAFActive ? {} : { onlyExceptionApplied }) };
    }
    return { ...params, ...sortParams, ...(isAFActive ? {} : { onlyExceptionApplied }) };
  }

  const getRequestParams = (isPagination = true, locCd = null, itemCd = null, customerCd = null) => {
    const params = {};
    const searchFilters = {};

    if (!isEmpty(rpVersionFilter)) {
      searchFilters.versionId = rpVersionFilter.id;
      const timeBuckets = rpVersionFilter?.planConfigObj?.TIME_BUCKETS;
      searchFilters.timeBuckets = !isEmpty(timeBuckets) ? JSON.stringify(timeBuckets) : null;
      searchFilters.dpVersionId = rpVersionFilter?.planConfigObj?.DP_VERSION_ID;
    }

    if (locCd != null) {
      searchFilters.locCd = [locCd];
    }

    if (itemCd != null) {
      searchFilters.item = itemCd;
    }
    if (customerCd != null) {
      searchFilters.customer = customerCd;
    }

    if (isPagination) {
      if (subCurrentPage) {
        searchFilters.page = subCurrentPage - 1;
      }

      if (subPageSize) {
        searchFilters.pageSize = subPageSize;
      }
      if (subMeasures) {
        searchFilters.activeMeasure = subMeasures;
      }
    }
    params.searchFilters = searchFilters;
    return { ...params };
  }

  const handleDimenClose = () => {
    setDimenModalOpen(false);
  };

  const setGridHeaders = (headers, headerMap, isMain) => {
    const mainColDefs = cloneDeep(defaultColumnDefs);
    const subColDefs = cloneDeep(defaultSubGridColumnDefs);
    mainBucketColumnDefs.current = [];
    subBucketColumnDefs.current = [];

    /* 이전 Column의 Status를 기억하기 때문에 ColumnDefs의 순서를 따라가지 않음에 따라 UK 설정 */
    const bucketPostFix = rpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + rpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;

    headers.forEach((header) => {
      if (isMain) {
        mainBucketColumnDefs.current.push({
          headerName: headerMap[header], field: header, width: 110, sortable: false,
          colId: header + bucketPostFix,
          suppressMovable: false,
          type: ["nonEditableColumn", "rightAligned"],
          cellStyle: params => dataCellStyle(params, headers),
          cellEditor: NumericEditor,
          valueFormatter: emptyValueFormatter
        });
      } else {
        subBucketColumnDefs.current.push({
          headerName: headerMap[header], field: header, width: 110, sortable: false,
          colId: header + bucketPostFix,
          suppressMovable: false,
          type: ["rightAligned"],
          cellStyle: (params) => subGridCellStyle(params),
          editable({ data }) {
          return data?.measure === mfMeasure.name;
          },
          cellEditor: NumericEditor,
          valueFormatter: emptyValueFormatter
        });
      }
    });

    if (isMain) {
      GridUtils.setColumnDefs({ ...mainGridRef.current, columnDefs: [...mainColDefs, ...mainBucketColumnDefs.current] });
    } else {
      GridUtils.setColumnDefs({ ...subGridRef.current, columnDefs: [...subColDefs, ...subBucketColumnDefs.current] });
    }
  }

  const setGridDatas = (metaData, formatHeaderList) => {
    const datas = [];
    if (!metaData) {
      return datas;
    }
    const results = metaData.pivotdata;
    const measureDict = metaData.measureDict;

    for (const result of results) {
      const measure = measureDict[result.measureCd];
      if (!subMeasures.includes(measure.measureCd)) {
        continue;
      }
      const row = {
        measure: measure.name,
        measureSeq: measure.seqNum,
        total: result.pivotData ? result.pivotData.reduce((a, b) => a + b, 0) : 0,
        editableRow: result.editable && result.editableCustomerHrchy,
        measureEditable: false,
        desc: 1,
        editableCustomerHrchy: result.editableCustomerHrchy,
        resultRow: result,
        customerCd: result.customerCd,
        customerNm: result.customerNm,
        itemCd: result.itemCd,
        itemNm: result.itemNm,
        invClassCd: result.invClassCd,
        locCd: result.locCd,
        locNm: result.locNm,
        measureCd: result.measureCd,
      };

      for (let i = 0; i < formatHeaderList.length; i++) {
        const header = formatHeaderList[i];
        if (result.pivotData) {
          row[header] = (result.pivotData[i] !== null && result.pivotData[i] !== undefined && !isNaN(result.pivotData[i]) && result.pivotData[i] !== 0)
            ? parseFloat(NumberUtil.formatNumber(result.pivotData[i], { maximumFractionDigits: 2, minimumFractionDigits: 0 }).replace(/,/g, ""))
            : null;
        }
      }
      datas.push(row);
    }
    return datas;
  };

  /* 하단(고객) 그리드 초기화 — 상단 그리드에 데이터가 없을 때 이전 조회 결과가 남지 않도록 한다 */
  const clearSubGrid = () => {
    subGridHeaderMap.current = {};
    cubeHeaders.current = [];
    subBucketColumnDefs.current = [];
    setSubGridData([]);
    setSubGridPages(0);
    setSelectedItem(null);
    setSelectedLoc(null);
    setSelectedCust(null);
    GridUtils.setColumnDefs({ ...subGridRef.current, columnDefs: defaultSubGridColumnDefs });
  };

  const retrieveDpCube = (locCd = null, itemCd = null, customerCd = null) => {
    subGridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams(true, locCd, itemCd, customerCd);
    rpChannelPlanningReviewService.getCustomers(params)
      .then(response => {
        const { resultData, headerMap, totalPages } = response.data;
        const { pivotdata } = resultData;

        if (pivotdata && !isEmpty(pivotdata)) {
          subGridHeaderMap.current = headerMap;
          cubeHeaders.current = resultData?.headers;
          setGridHeaders(resultData?.headers, headerMap, false);
          const gridData = setGridDatas(resultData, resultData?.headers);
          setSubGridData(gridData);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          setSubGridData([]);
        }
        setSubGridPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { filter: filterValue,
          currentPage, pageSize, subCurrentPage, subPageSize, locCd, itemCd });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .finally(() => {
        simulationUpdateDatasRef.current = [];
        subGridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const setAttributeDatas = (resultsData) => {
    const itemAttr = "itemAttr";
    const custAttr = "custAttr";

    if (size(resultsData?.attributes) > 0) {
      for (let i = 0; i < resultsData.attributes.length; i++) {
        resultsData[itemAttr + i] = resultsData?.attributes?.[i];
      }
    }

    if (size(resultsData?.custAttributes) > 0) {
      for (let i = 0; i < resultsData.custAttributes.length; i++) {
        resultsData[custAttr + i] = resultsData?.custAttributes?.[i];
      }
    }
  }

  const setRpGridDatas = (headers, results, measureCds = null) => {
    const datas = [];
    const rpOrderQtyPivotData = "rpOrderQtyPivotData";
    const arrivalQtyPivotData = "arrivalQtyPivotData";
    const arrivalQtyPlanPivotData = "arrivalQtyPlanPivotData";
    const arrivalQtyCfmPivotData = "arrivalQtyCfmPivotData";
    const demandQtyPivotData = "demandQtyPivotData";
    const salesQtyPivotData = "salesQtyPivotData";
    const salesQtyPlanPivotData = "salesQtyPlanPivotData";
    const salesQtyCfmPivotData = "salesQtyCfmPivotData";
    const salesQtyFcstPivotData = "salesQtyFcstPivotData";
    const salesQtyReplPivotData = "salesQtyReplPivotData";
    const bohQtyPivotData = "bohQtyPivotData";
    const eohQtyPivotData = "eohQtyPivotData";
    const targetStockQtyPivotData = "targetStockQtyPivotData";
    const kitFcstDemandQtyPivotData = "kitFcstDemandQtyPivotData";
    const fcstDemandQtyPivotData = "fcstDemandQtyPivotData";
    const replDemandQtyPivotData = "replDemandQtyPivotData";
    const reorderQtyPivotData = "reorderQtyPivotData";
    const backorderQtyPivotData = "backorderQtyPivotData";

    const measure = isNull(measureCds) ? isEmpty(measures) ? isEmpty(p13Measures) ? allMeasure : p13Measures : measures
      : measureCds;

    for (const resultsData of results) {
      for (const measureName of measure) {
        const display = displayName?.[measureName];
        const data = { ...resultsData,
          measure: display != null && !isEmpty(display) ? displayName[measureName] : measureName };
        setAttributeDatas(data);

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];

          switch (measureName) {
            case RP_ORDER:
              data[header] = resultsData[rpOrderQtyPivotData][idx];
              break;
            case ARRIVAL_QTY:
              data[header] = resultsData[arrivalQtyPivotData][idx];
              break;

            case ARRIVAL_QTY_PLAN:
              data[header] = resultsData[arrivalQtyPlanPivotData][idx];
              break;

            case ARRIVAL_QTY_CFM:
              data[header] = resultsData[arrivalQtyCfmPivotData][idx];
              break;

            case KIT_FCST_DEMAND_QTY:
              data[header] = resultsData[kitFcstDemandQtyPivotData][idx];
              break;

            case DEMAND_QTY:
              data[header] = resultsData[demandQtyPivotData][idx];
              break;

            case FCST_DEMAND_QTY:
              data[header] = resultsData[fcstDemandQtyPivotData][idx];
              break;

            case INTERNAL_DEMAND_QTY:
              data[header] = resultsData[replDemandQtyPivotData][idx];
              break;

            case SHIPMENT_QTY:
              data[header] = resultsData[salesQtyPivotData][idx];
              break;

            case SHIPMENT_QTY_PLAN:
              data[header] = resultsData[salesQtyPlanPivotData][idx];
              break;

            case SHIPMENT_QTY_CFM:
              data[header] = resultsData[salesQtyCfmPivotData][idx];
              break;

            case SHIPMENT_QTY_FCST:
              data[header] = resultsData[salesQtyFcstPivotData][idx];
              break;

            case SHIPMENT_QTY_REPL:
              data[header] = resultsData[salesQtyReplPivotData][idx];
              break;

            case BOH:
              data[header] = resultsData[bohQtyPivotData][idx];
              break;

            case EOH:
              data[header] = resultsData[eohQtyPivotData][idx];
              break;

            case TARGET_STOCK:
              data[header] = resultsData[targetStockQtyPivotData][idx];
              break;

            case ROP:
              data[header] = resultsData[reorderQtyPivotData][idx];
              break;

            case BACKORDER_QTY:
              data[header] = resultsData[backorderQtyPivotData][idx];
              break;

            case INV_COV_DAYS:
              data[header] = getInvCovDays(idx, headers, resultsData, false);
              break;

            default:
              break;
          }
        }
        datas.push(data);
      }
    }
    return datas;
  }

  const retrieveRpResult = (layoutParam = {}, measureCds = null) => {
    mainGridRef.current?.api?.setGridOption('loading', true);
    const params = getRpRequestParams();

    rpChannelPlanningReviewService.getAll(params)
      .then(response => {
        const { pivotResult, totalPages, totalItems } = response.data;
        const { localDateHeader, headerMap, data } = pivotResult;
        if (pivotResult?.data && !isEmpty(pivotResult.data)) {
          gridHeaderMap.current = headerMap;
          setGridHeaders(localDateHeader, headerMap, true);
          const datas = setRpGridDatas(localDateHeader, data, measureCds);
          setSimulationData(datas);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });

          setSimulationData([]);
          // 상단 그리드가 비면 선택된 행도 없으므로 하단(고객) 그리드도 같이 비운다 — 이전 조회 결과가 남는 것을 방지
          clearSubGrid();
        }
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { currentPage, pageSize, dimensionAttrData });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...filterState }));
      })
      .finally(() => {
        mainGridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const retrieveAttributeConfig = () => {
    p13nService.get("PSI_SIMULATION_ETC")
        .then(response => {
          if (response.data) {
            const attrConfig = response?.data?.ATTRIBUTE;
            setDimensionAttrData(attrConfig ? response.data.ATTRIBUTE : {});
          }
        })
        .catch(e => {
          debug(e);
          setDimensionAttrData({});
        });
  };

  const retrieveMeasures = () => {
    const list = [SA_QTY.cd, ASA_QTY.cd, BF_QTY.cd, MF_QTY.cd];
    measureService.getAll({ isBasic: true })
      .then(response => {
        if (response.data?.results) {
          const result = response.data.results;
          const filteredReseult = result.filter(re => !re.simFlg && list.includes(re.measureCd));
          setSubAllMeasure(filteredReseult.map(re => re.name));
          setSubAllMeasureObj(filteredReseult);
          const mfQty = filteredReseult.filter(el => el.measureCd === MF_QTY.cd);
          if (mfQty.length > 0) {
            setMfMeasure(mfQty[0]);
          }
        }
      })
      .catch(e => {
        debug(e);
      })
  }

  /* 서브 그리드(고객사별 DP cube)에서 편집된 행을 Workbench.js의 saveTarget과 동일한 형태로 변환 */
  const buildDpCubeSaveTarget = () => {
    return subGridData
      .filter(row => row.edited && Object.keys(row.edited).length > 0)
      .map(row => ({
        ...row.resultRow,
        custConfigCd: CodeUtil.customer.cd,
        itemConfigCd: CodeUtil.item.cd,
        edited: row.edited,
        versionDto: dpVersionRef.current,
      }));
  }

  const saveDpCubeData = (dpSaveTarget, openRunModalAfterSave = false) => {
    setBackDropOpen(true);
    demandValService.upsertScenario(dpSaveTarget)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        retrieveDpCube(selectedLoc, selectedItem, selectedCust);
        if (openRunModalAfterSave) {
          setRunModalOpen(true);
        }
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
      })
      .finally(() => {
        setBackDropOpen(false);
      });
  }

  const retrieveItemHrchies = () => {
    itemHrchyService.getAll({ searchFilters: { hrchyNum: CodeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const { results } = response.data;
        if (results !== null) {
          setDefaultValue(results);
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const handleRunClick = () => {
    if (!dpVersionRef.current) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotExistConfiguredDpVersion") });
      return;
    }

    if (isEmpty(buildDpCubeSaveTarget())) {
      setRunModalOpen(true);
    } else {
      setDialogInfo({ open: true, actionName: "SAVE_AND_RUN", title: "confirm", content: t("MSG_SaveDpAndRunConfirm") });
    }
  }

  const runRp = () => {
    setBackDropOpen(true);
    setIsRun(false);

    if (selectItemHrchy !== null && !isEmpty(selectItemHrchy)) {
      rpVersionFilter.targetItemHrchy = selectItemHrchy.map(item => item.hrchyCd);
      setDefaultValue(selectItemHrchy);
    }

    const updatedVersion = {
      ...rpVersionFilter,
      planConfigObj: {
        ...rpVersionFilter.planConfigObj,
        USE_SHIPMENT_TXN: useShipmentTxn,
        USE_MP_PRODUCTION_PLAN: useMpProductionPlan,
        PLAN_EXEC_DATE: rpVersionFilter?.planConfigObj?.PLAN_EXEC_DATE ?? moment().format('YYYY-MM-DD')
      }
    };

    runRpService.runRp(updatedVersion)
      .then(response => {
        const { count } = response.data;
        if (count > 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_RpSuccess") });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoTargetRpPlan") });
        }
        retrieveDpCube(selectedLoc, selectedItem, selectedCust);
      })
      .catch(e => {
        const { status, data } = e?.response ?? {};
        if (status === 409) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.optimisticLock, content: t("MSG_OptimisticLockAboutTarget", { target: rpVersionFilter?.name }) });
        } else if (status === 400) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t(data.message) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_RpFailed") });
        }
        debug(e);
      })
      .finally(() => {
        reloadRpVersionFilter(reduxDispatch);
        setBackDropOpen(false);
      });
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      const dpSaveTarget = buildDpCubeSaveTarget();
      // const hasSimulationUpdate = !isEmpty(simulationUpdateDatasRef.current);
      const hasDpCubeUpdate = !isEmpty(dpSaveTarget);

      if (!hasDpCubeUpdate) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
        return;
      }

      if (hasDpCubeUpdate) {
        saveDpCubeData(dpSaveTarget);
      }
    } else if (action === "SAVE_AND_RUN") {
      saveDpCubeData(buildDpCubeSaveTarget(), true);
    }
  }

  const onGridReady = (params, target) => {
    setGridLoading(true);
    if (isEqual(target, MAIN_GRID)) {
      GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true });
    } else {
      GridUtils.setColumnDefs({ ...params, columnDefs: defaultSubGridColumnDefs, target, initState: true });
    }
  }

  const onRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      const filterInfo = extractFilterInfo(params, t, mainGridRef);
      getFilterStore(viewName).setValue(filterInfo);

      const leafChildren = params.node.allLeafChildren ? params.node.allLeafChildren : params.node.parent.allLeafChildren;
      // // 선택된 노드에서 locCd, itemCd 추출
      if (leafChildren && leafChildren.length > 0) {
        const firstNode = leafChildren[0];
        const locCd = firstNode.data?.destLocCd;
        const itemCd = firstNode.data?.itemCd;
        const customerCd = firstNode.data?.customerCd;
        // locCd, itemCd가 있으면 getDpCube 조회
        if (locCd && itemCd) {
          setSelectedItem(itemCd);
          setSelectedLoc(locCd);
          setSelectedCust(customerCd ?? null);
          retrieveDpCube(locCd, itemCd, customerCd);
        }
      }
    }
  }

  const onSubGridRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      const selectedNodes = params.api.getSelectedNodes();

      const filterInfo = extractFilterInfo(params, t, subGridRef);
      const view = "Sub_"+ viewName;
      getFilterStore(view).setValue(filterInfo);

      const leafChildren = params.node.allLeafChildren ? params.node.allLeafChildren : params.node.parent.allLeafChildren;

      params?.api?.setNodesSelected({ nodes: selectedNodes, newValue: false });
      params?.api?.setNodesSelected({ nodes: leafChildren, newValue: true });
    }
  }

  const initCheck = (params) => {
    const rowCount = params.api.getDisplayedRowCount();
    if (rowCount === 0) {
    } else {
      const firstNode = params.api.getDisplayedRowAtIndex(0);
      if (firstNode) {
        const selected = firstNode.allLeafChildren ? firstNode.allLeafChildren : firstNode.parent?.allLeafChildren ?? [];
        if (selected && selected.length > 0) {
          params.api.setNodesSelected({ nodes: [selected[0]], newValue: true, source: CHECKBOX_SELECTED });
        }
      }
    }
  }

  const applyMeasures = selectedMeasures => {
    setP13Measures(selectedMeasures)
    setMeasures(selectedMeasures);
  }

  const applySubMeasures = (selectedSubMeasures) => {
    const measureCds = [];
    if (selectedSubMeasures) {
      for (const measure of subAllMeasureObj) {
        if (selectedSubMeasures.includes(measure.name)) {
          measureCds.push(measure.measureCd);
        }
      }
      setSubMeasures(measureCds);
    }
  }
  /* params: 수정한곳의 params, subGridParentParams: 서브 그리드에서 수정시 상위 컴포넌트 Params */
  const onCellValueChanged = params => {
    const { column, data, value, oldValue, node, api } = params;
    const numValue = Number(value);

    const headerIndex = findIndex(cubeHeaders.current, header => isEqual(header, column.colDef.field));
    if (headerIndex > -1) {
      const pivotData = { ...data.pivotData };
      if (!(headerIndex in pivotData)) {
        pivotData[headerIndex] = oldValue;
      }
      data.pivotData = pivotData;

      const edited = { ...data.edited };
      if (isEqual(Number(pivotData[headerIndex]), numValue)) {
        delete edited[headerIndex];
      } else {
        edited[headerIndex] = numValue;
      }
      data.edited = edited;

      // cellStyle이 즉시 재평가되도록 강제 refresh (다음 셀 클릭 전까지 파란색이 반영되지 않는 문제 방지)
      api.refreshCells({ rowNodes: [node], columns: [column], force: true });
    }
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveRpResult();
    }
  }

  const mainStatusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        },
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subStatusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName: "Sub_" + viewName
          },
          align: "left",
          key: "filterStatusPanel"
        },
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCustomConfig = () => {
    companyService.getCustomConfig()
      .then(response => {
        if (response.data) {
          setAttrConfig(response.data);
        } else {
          setAttrConfig({})
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulationData, viewName]);

  useEffect(() => {
    const view =  "Sub_" + viewName;
    getFilterStore(view).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subGridData, viewName]);

  const defaultMainGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: defaultColumnDefs });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultSubGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: defaultSubGridColumnDefs });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    retrieveAttributeConfig();
    getCustomConfig();
    retrieveMeasures();
    retrieveItemHrchies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isRun) {
      runRp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRun]);

  useEffect(() => {
    if (rpVersionFilter) {
      if (rpVersionFilter?.statusCd === PLANNED && rpVersionFilter?.execTs !== null) {
        setUseShipmentTxn(rpVersionFilter?.planConfigObj?.USE_SHIPMENT_TXN ?? false);
        setUseMpProductionPlan(rpVersionFilter?.planConfigObj?.USE_MP_PRODUCTION_PLAN ?? false);
      } else {
        setUseShipmentTxn(false);
        setUseMpProductionPlan(false);
      }
    }
  }, [rpVersionFilter]);

  // 페이지·정렬 변경은 최초 조회(initLoad)가 끝난 뒤에만 재조회한다
  useEffect(() => {
    if (gridLoading && !isEmpty(rpVersionFilter) && !isEmpty(measures) && initLoad && dimensionAttrData !== null && attrConfig !== null) {
      retrieveRpResult();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortParams]);


  useEffect(() => {
    if (gridLoading && !isEmpty(rpVersionFilter) && !isEmpty(subMeasures)) {
      retrieveDpCube(reduxViewState?.locCd, reduxViewState?.itemCd, reduxViewState?.customerCd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCurrentPage, subPageSize, gridLoading, subMeasures])

  /*
   * 최초 조회 + 필터 변경 시 재조회. 조회 선행조건(gridLoading·버전·measure·attr 설정)이
   * 비동기로 도착하므로 deps 에 전부 넣어야, 마지막으로 도착한 값이 첫 조회를 트리거한다.
   * (조건이 다 갖춰지기 전엔 아무것도 하지 않으므로 중복 조회는 생기지 않는다)
   * initLoad 는 "최초 조회를 했는지" 표시일 뿐이라 여기서 조건으로 쓰면(!initLoad)
   * 첫 조회 이후 필터를 바꿔도 재조회가 되지 않는다.
   */
  useEffect(() => {
    if (gridLoading && !isEmpty(rpVersionFilter) && !isEmpty(measures) && dimensionAttrData !== null && attrConfig !== null) {
      initPageToRetrieve();
      setInitLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, rpVersionFilter?.id, filterValue, measures, appliedExceptions, onlyExceptionApplied, advancedFilters, isAFActive, dimensionAttrData, attrConfig]);

  useEffect(() => {
    const dpVersionId = rpVersionFilter?.planConfigObj?.DP_VERSION_ID;
    if (!dpVersionId) {
      setDpVersionStatusCd(null);
      dpVersionRef.current = null;
      return;
    }
    planVersionService.get(dpVersionId)
      .then(response => {
        setDpVersionStatusCd(response?.data?.statusCd ?? null);
        dpVersionRef.current = response?.data ?? null;
      })
      .catch(e => {
        setDpVersionStatusCd(null);
        dpVersionRef.current = null;
        debug(e);
      });
  }, [rpVersionFilter?.planConfigObj?.DP_VERSION_ID]);

  useEffect(() => {
    if (reduxViewState) {
      const currentReduxViewState = cloneDeep(reduxUtil.getViewState(viewName));
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...currentReduxViewState }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const onCellSelected = () => {
    const api = subGridRef.current.api;
    const ranges = api.getCellRanges();

    if (ranges && ranges.length > 0) {
      const firstRange = ranges[0];
      const columns = firstRange.columns;
      const colIds = [];
      for (const col of columns) {
        colIds.push(col?.colDef?.field);
      }
      selectedCellId.current = colIds
    }
  }

  useEffect(() => {
    let filter = filterValue;
    if (filterData.filterList) {
      setItemFilterList(filterData.filterList ?? {});
      setItemCustomValue([]);
      if (isEmpty(filterValue?.category)) {
        setFilterValue({ ...filterValue, category: [] });
        filter = { ...filterValue, category: [] };
      } else {
        setTreeLoad(true);
      }
      setItemFilterType(filterData.filterType);
    }

    if (reduxViewState) {
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, filter, itemFilterType: filterData?.filterType, itemFilterList: filterData?.filterList }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  useEffect(() => {
    if (itemFilterList && Object.keys(itemFilterList)?.length > 0) {
      setItemFilterColor(muicolor.INFO);
    } else {
      setItemFilterColor(muicolor.DEFAULT);
    }
    if (reduxViewState) {
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, itemFilterType, itemFilterList }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemFilterList]);

  useEffect(() => {
    let filter = filterValue;
    if (customerFilterData.filterList) {
      setCustomerFilterList(customerFilterData.filterList ?? {});
      setCustomerCustomValue([]);
      if (isEmpty(filterValue?.customerHrchys)) {
        setFilterValue({ ...filterValue, customerHrchys: [] });
        filter = { ...filterValue, customerHrchys: [] };
      } else {
        setCustTreeLoad(true);
      }
      setCustFilterType(customerFilterData.filterType);
    }

    if (reduxViewState) {
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, filter, custFilterType: customerFilterData?.filterType, customerFilterList: customerFilterData?.filterList }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerFilterData]);

  useEffect(() => {
    if (customerFilterList && Object.keys(customerFilterList)?.length > 0) {
      setCustomerFilterColor(muicolor.INFO);
    } else {
      setCustomerFilterColor(muicolor.DEFAULT);
    }
    if (reduxViewState) {
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, custFilterType, customerFilterList }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerFilterList]);

  // 버전 변경은 위 필터 useEffect(deps 에 rpVersionFilter?.id 포함)에서 재조회하므로 여기서 다시 부르지 않는다(중복 조회 방지)

  return (
    <>
      <FilterContainer>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={[SIMULATION, PLANNED, RELEASED]} />
        <LocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} customValue={itemCustomValue} treeload={treeLoad} enableMultiSelect selectLeafs />
        <IconButton aria-describedby={"itemFilterPop"} size="small" sx={{ mr: 1 }} onClick={filterActionItem} color={itemFilterColor}>
          <Tooltip title={t("itemFilter")}><FilterAlt /></Tooltip>
        </IconButton>
        <CustomerHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} customValue={customerCustomValue} treeload={custTreeLoad} enableMultiSelect selectLeafs />
        <IconButton aria-describedby={"customerFilterPop"} size="small" sx={{ mr: 1 }} onClick={filterActionCustomer} color={customerFilterColor}>
          <Tooltip title={t("customerFilter")}><FilterAlt /></Tooltip>
        </IconButton>
        <ZeroExclusionFilter appliedExceptions={appliedExceptions} setAppliedExceptions={handleAppliedExceptionsChange} />
        <FormControlLabel
          control={<Checkbox color="primary" name={"exceptionsOnly"} checked={onlyExceptionApplied} onChange={handleExceptionsOnlyChange} />}
          label={t("exceptionsOnly")} key="EXCEPTIONS_ONLY" sx={{ display: "flex" }}
        />
        <Box sx={{ ml: "auto" }}>
          <RunButton label="runReplenishPlan" disabled={isEmpty(rpVersionFilter)} onClick={handleRunClick} />
          <FilterButton label="advancedFilter" advancedFilterColor={isAFActive ? muicolor.INFO : muicolor.DEFAULT} onClick={handleFilterOpen} />
        </Box>
      </FilterContainer>
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
      <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
        <SplitterContainer initialSizes={[50, 50]}>
          <Box className="ag-theme-alpine" sx={{ height: "100%" }}>
            <Box className="ag-theme-balham non-background-selected-row" mb={1} sx={{ height: "calc(100% - 50px)" }}>
              <AgGridReact
                ref={mainGridRef}
                rowData={simulationData}
                {...defaultMainGridMemo}
                columnDefs={mainGridColumnDefs}
                groupDisplayType={'custom'}
                groupDefaultExpanded={-1}
                groupHideOpenParents
                rowSelection={{
                  ...defaultMainGridMemo.rowSelection,
                  groupSelects: "descendants",
                }}
                statusBar={mainStatusBar}
                onRowSelected={onRowSelected}
                onGridReady={(e) => onGridReady(e, MAIN_GRID)}
                // onCellClicked={onCellGridClicked}
                onRowDataUpdated={initCheck}
                onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
                cellSelection={{
                  range: true,
                  suppressMultiRanges: true
                }}
              >
              </AgGridReact>
            </Box>
            <PaginationContainer
              currentPage={currentPage} totalPages={gridPages}
              pageSize={pageSize} pageSizes={pageSizes}
              setCurrentPage={setCurrentPage} setPageSize={setPageSize}
            />
          </Box>
          <Box className="ag-theme-alpine" sx={{ height: "100%" }}>
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <SaveButton disabled={isEmpty(rpVersionFilter)} onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
            </Box>
            <Box className="ag-theme-balham non-background-selected-row" mt={1} mb={2} sx={{ height: "calc(100% - 120px)" }}>
              <AgGridReact
                ref={subGridRef}
                {...defaultSubGridMemo}
                // columnDefs={defaultSubGridColumnDefs}
                groupDisplayType="custom"
                groupDefaultExpanded={-1}
                groupHideOpenParents
                rowSelection={{
                  ...defaultSubGridMemo.rowSelection,
                  groupSelects: "descendants",
                }}
                statusBar={subStatusBar}
                onGridReady={(e) => onGridReady(e, SUB_GRID)}
                onCellValueChanged={onCellValueChanged}
                rowData={subGridData}
                onRowDataUpdated={initCheck}
                onRowSelected={onSubGridRowSelected}
                // getContextMenuItems={getContextMenuItems}
                onCellSelectionChanged={onCellSelected}
                // onCellClicked={onSubGridCellClicked}
              />
            </Box>
            <PaginationContainer
              currentPage={subCurrentPage} totalPages={subGridPages}
              pageSize={subPageSize} pageSizes={pageSizes}
              setCurrentPage={setSubCurrentPage} setPageSize={setSubPageSize}
            />
          </Box>
        </SplitterContainer>
      </Box>
      {openFilter && (
        <>
          <ResizableFilterPanel>
            <AdvancedFilter
              columnDefs={advancedFilterColumnDefs}
              advancedFilters={pendingAdvancedFilters ?? advancedFilters}
              onApply={handleAdvancedFilterApply}
              viewName={viewName}
              lockedFilterNames={["baseDate"]}
              onClose={() => setOpenFilter(false)}
            />
          </ResizableFilterPanel>
        </>
      )}
      </Box>
      <Backdrop sx={{ color: '#fff', zIndex: 1400 }} open={backDropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        actionName={dialogInfo.actionName}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <RPFilterModal
        open={filterModalOpen}
        close={handelFilterClose}
        displayModButtons
        modelCode={item.name}
        modalResultData={setFilterData}
        filterTypes={{ Item: itemFilterType ?? [filterType.or.cd, filterType.equal.cd] }}
        filteredList={{ Item: itemFilterList ?? {} }}
      />
      <RPFilterModal
        open={customerFilterModalOpen}
        close={handelCustomerFilterClose}
        displayModButtons
        modelCode={customer.name}
        modalResultData={setCustomerFilterData}
        filterTypes={{ Customer: custFilterType ?? [filterType.or.cd, filterType.equal.cd] }}
        filteredList={{ Customer: customerFilterList ?? {} }}
      />
      <RunModal
        title={"runReplenishPlan"}
        viewName={viewName}
        modalOpen={runModalOpen}
        setModalOpen={setRunModalOpen}
        defaultValue={defaultValue}
        setSelectedItemHrchy={setSelectItemHrchy}
        selectedPlanVersion={rpVersionFilter}
        setIsRun={setIsRun}
        useShipmentTxn={useShipmentTxn}
        onUseShipmentTxnChange={setUseShipmentTxn}
        useMpProductionPlan={useMpProductionPlan}
        onUseMpProductionPlan={setUseMpProductionPlan}
      />
      {/* Main Grid Measure Modal */}
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        applyMeasures={applyMeasures}
        displayNames={setDisplayName}
        defaultMeasure={defaultMeasure}
        hasDisplayName={true}
      />
      {/* Sub Grid Measure Modal */}
      <MeasureModal
        open={subMeasureModalOpen}
        setOpen={setSubMeasureModalOpen}
        viewName={`${viewName}-sub`}
        allMeasure={subAllMeasure}
        applyMeasures={applySubMeasures}
        key={subAllMeasure.length}
      />
      <DimensAttrModal
        open={dimenModalOpen}
        close={handleDimenClose}
        viewName="PSI_SIMULATION_ETC"
        dimensionAttrData={dimensionAttrData}
        setDimensionAttrData={setDimensionAttrData}
        selectLv={{}}
        attrConfig={attrConfig}
        attrTarget={dimenAttrTarget}
      />
    </>
  );
};

export default withTranslation()(ChannelPlanningReview);
